/* ============================================================================
 * types.ts - Type definitions for ProjectDataContext
 * ============================================================================ */

import type { 
  ProjectData, 
  ProjectNode, 
  ProjectNodeWithComputed,
  NodeOperation, 
  ViewFilter 
} from '@/shared/types/projectData';
import type { 
  WebSocketState, 
  SyncState 
} from '@/shared/api/WebSocketManager';
import type { PerformanceReport } from '@/shared/utils/snapshotPerformanceAnalyzer';

/** Opt in to an acknowledged write; unsupported operations reject before mutation. */
export interface OperationPersistenceOptions {
  requireConfirmed?: boolean;
}

export interface NodeUpdateOptions extends OperationPersistenceOptions {
  expectedFields?: Partial<ProjectNode>;
}

export class ConfirmedWriteError extends Error {
  constructor(message: string, public readonly outcome: 'rejected' | 'unknown' | 'not-dispatched', public readonly code?: string) {
    super(message);
    this.name = 'ConfirmedWriteError';
  }
}

/**
 * Main context type defining all available functions and state
 */
export interface ProjectDataContextType {
  projectData: ProjectData | null;
  treeNodes: ProjectNode[];
  nodeMap: Map<string, ProjectNode>;
  expandedNodeIds: Set<string>;
  selectedNodeIds: Set<string>;
  applyOp: (operation: NodeOperation, options?: OperationPersistenceOptions) => Promise<void>;
  captureOperation: (
    type: string,
    category: 'view' | 'selection' | 'filter' | 'navigation' | 'preference' | 'interaction' | 'edit',
    before: unknown,
    after: unknown,
    description?: string,
    component?: string,
    skipHistory?: boolean
  ) => void;
  moveNodeTo: (nodeId: string, targetParentId: string | null, targetIndex: number) => Promise<void>;
  updateNode: (nodeId: string, updates: Partial<ProjectNode>, options?: NodeUpdateOptions) => Promise<void>;
  batchUpdateNodes: (updates: Array<{ nodeId: string; updates: Partial<ProjectNode> }>, options?: OperationPersistenceOptions) => Promise<void>;
  moveGroup: (groupId: string, daysDelta: number) => Promise<void>;
  deleteNode: (nodeId: string) => Promise<void>;
  deleteNodes: (nodeIds: string[]) => Promise<void>;
  createNode: (node: Partial<ProjectNode>, parentId?: string, index?: number) => Promise<void>;
  getNode: (nodeId: string) => ProjectNode | null;
  getVisibleNodes: () => ProjectNode[];
  toggleExpanded: (nodeId: string) => void;
  selectNode: (nodeId: string, multi?: boolean) => void;
  expandAll: () => void;
  collapseAll: () => void;
  getRenderableNodes: () => (ProjectNodeWithComputed & { isVisible: boolean })[];
  filters: ViewFilter;
  setFilters: (filters: ViewFilter) => void;
  filteredNodes: ProjectNodeWithComputed[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortBy: string | null;
  setSortBy: (sortBy: string | null) => void;
  isCompactView: boolean;
  setIsCompactView: (isCompact: boolean) => void;
  canUndo: boolean;
  canRedo: boolean;
  clearHistory: () => Promise<{ success: boolean }>;
  importProjectData: (data: ProjectData) => Promise<{ success: boolean; projectId: string; message: string }>;
  // Atomic operations for better history tracking
  updateProgress: (nodeId: string, progress: number) => Promise<void>;
  isUndoRedoInProgress: boolean;
  // Undo/redo functions
  undo: () => Promise<void>;
  redo: () => Promise<void>;
  jumpToHistoryPosition: (position: number) => Promise<void>;
  getUndoHistoryList: () => Array<{
    name: string;
    timestamp: number;
    category: string;
    isCurrentPosition: boolean;
    canJumpTo: boolean;
    metadata?: Record<string, unknown>;
  }>;
  undoState: {
    canUndo: boolean;
    canRedo: boolean;
    position: number;
    stackSize: number;
  };
  // WebSocket and sync state
  wsState: WebSocketState;
  syncState: SyncState;
  reconnectWebSocket: () => void;
  retrySyncRecovery?: () => Promise<void>;
  // Version checking and sync
  checkServerVersion: () => Promise<number | null>;
  syncWithServer: () => Promise<void>;
  hasUnsavedChanges: boolean;
  localCacheSaveFailed?: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  // Snapshot performance monitoring
  getSnapshotPerformanceReport: () => {
    metrics: PerformanceReport['metrics'];
    recommendations: string[];
    healthScore: number;
  };
  configureSnapshotHistory: (options: {
    maxSnapshots?: number;
    enableCompression?: boolean;
    enablePerformanceMonitoring?: boolean;
    backgroundProcessing?: boolean;
  }) => void;
}
